import { useContext } from 'react';
import { ShareModalContext, ShareModalContextType } from './share-modal-context';

/**
 * Hook to access share modal functionality. Must be used within ShareModalProvider.
 *
 * @returns {Object} Share modal methods and state
 * @returns {Function} openModal - Opens the share modal with provided options
 * @returns {Function} closeModal - Closes the share modal
 *
 * @example
 * const { openModal, closeModal } = useShareModal();
 *
 * // Basic usage - will only display a copy link button
 * openModal({
 *   title: 'Share this page',
 *   subtitle: 'Share this content with your friends',
 *   onCopyLinkClick: () => handleCopyLink(),
 * });
 *
 * // With all sharing options
 * openModal({
 *   title: 'Share wedding website',
 *   subtitle: 'Share your special day',
 *   onCopyLinkClick: () => handleCopyLink(),
 *   onFacebookClick: () => handleFacebookShare(),
 *   onTwitterClick: () => handleTwitterShare(),
 *   CustomComponent: <AdditionalContent />
 * });
 *
 * // Buttons are only rendered if corresponding handlers are provided
 * openModal({
 *   title: 'Share',
 *   onCopyLinkClick: () => {}, // Only shows Copy button
 * });
 *
 * @throws {Error} If used outside of ShareModalProvider
 */
export function useShareModal(): ShareModalContextType {
  const context = useContext(ShareModalContext);
  if (!context) {
    throw new Error('useShareModal must be used within ShareModalProvider');
  }
  return context;
}
